import React, { useState } from 'react';
import { Header } from '../components/ui/Header';
import { SearchBar } from '../components/ui/SearchBar';
import { EnhancedAIAnalysis } from '../components/ai/EnhancedAIAnalysis';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { tradierService, financialDataService } from '../services';
import type { StockQuote, CompanyMetrics } from '../types/market';

export const AIAnalysisDemo: React.FC = () => {
  const { user, signOut } = useAuth();
  const { showToast } = useToast();
  
  const [selectedStock, setSelectedStock] = useState<{
    quote: StockQuote;
    metrics: CompanyMetrics;
  } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleStockSelect = async (symbol: string) => {
    setLoading(true);
    try {
      // Fetch stock data and company metrics
      const [quotes, metrics] = await Promise.allSettled([
        tradierService.instance.getQuotes([symbol.toUpperCase()]),
        financialDataService.instance.getCompanyMetrics(symbol.toUpperCase())
      ]);
      
      if (quotes.status === 'fulfilled' && quotes.value.length > 0) {
        const quote = quotes.value[0];
        let companyMetrics: CompanyMetrics;
        
        if (metrics.status === 'fulfilled') {
          companyMetrics = metrics.value;
        } else {
          // Fallback metrics
          companyMetrics = {
            symbol: symbol.toUpperCase(),
            name: `${symbol.toUpperCase()} Stock`,
            marketCap: 0,
          };
        }
        
        setSelectedStock({ quote, metrics: companyMetrics });
        showToast(`Loaded ${symbol.toUpperCase()} for AI analysis`, 'success');
      } else {
        throw new Error(`No data found for ${symbol}`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load stock data';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const popularStocks = [
    { symbol: 'AAPL', name: 'Apple Inc.' },
    { symbol: 'MSFT', name: 'Microsoft Corp.' },
    { symbol: 'GOOGL', name: 'Alphabet Inc.' },
    { symbol: 'AMZN', name: 'Amazon.com Inc.' },
    { symbol: 'TSLA', name: 'Tesla Inc.' },
    { symbol: 'NVDA', name: 'NVIDIA Corp.' },
    { symbol: 'META', name: 'Meta Platforms Inc.' },
    { symbol: 'NFLX', name: 'Netflix Inc.' },
  ];

  return (
    <div className="page-container">
      <Header 
        user={user}
        onLogout={signOut}
        title="Enhanced AI Analysis Demo - Nitrextrader"
      />
      
      <main className="page-content">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">
            Enhanced AI Stock Analysis
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-4 sm:mb-6">
            Comprehensive stock analysis using fundamental valuation, technical analysis, and sentiment assessment
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6 mb-6 sm:mb-8">
            <div className="card">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Fundamental Valuation</h3>
              <p className="text-gray-600 text-xs sm:text-sm">
                DCF analysis, relative multiples (P/E, EV/EBITDA), dividend models, and margin of safety calculations
              </p>
            </div>
            
            <div className="card">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Technical Analysis</h3>
              <p className="text-gray-600 text-xs sm:text-sm">
                Moving averages, RSI, MACD, support/resistance levels, and optimal entry/exit timing
              </p>
            </div>
            
            <div className="card">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Sentiment & Catalysts</h3>
              <p className="text-gray-600 text-xs sm:text-sm">
                Market sentiment, news flow, earnings catalysts, and institutional ownership trends
              </p>
            </div>
          </div>
        </div>

        <section className="content-section mb-3 sm:mb-6">
          <div className="section-content">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Select a Stock for Analysis</h2>
            <div className="mb-4 sm:mb-6">
              <SearchBar
                onSelect={(result) => handleStockSelect(result.symbol)}
                placeholder="Search for a stock symbol (e.g., AAPL, MSFT, GOOGL)..."
                className="w-full"
              />
            </div>

            <div>
              <h3 className="text-base sm:text-lg text-gray-600 mb-3 sm:mb-4">Popular Stocks</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 sm:gap-3">
                {popularStocks.map((stock) => (
                  <button
                    key={stock.symbol}
                    onClick={() => handleStockSelect(stock.symbol)}
                    disabled={loading}
                    className="p-2 sm:p-3 text-center border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="text-sm sm:text-base font-semibold text-gray-900">{stock.symbol}</div>
                    <div className="text-xs text-gray-600 mt-1 hidden sm:block">{stock.name}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {loading && (
          <div className="card text-center">
            <div className="inline-flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-sm sm:text-base">Loading stock data...</span>
            </div>
          </div>
        )}

        {selectedStock && !loading && (
          <>
            <section className="content-section">
              <div className="section-content">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{selectedStock.quote.symbol}</h2>
                    <p className="text-sm sm:text-base text-gray-600">{selectedStock.metrics.name}</p>
                  </div>
                  <div className="text-left sm:text-right">
                    <div className="text-2xl sm:text-3xl font-bold text-gray-900">
                      ${selectedStock.quote.price.toFixed(2)}
                    </div>
                    <div className={`text-base sm:text-lg font-semibold ${
                      selectedStock.quote.change >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {selectedStock.quote.change >= 0 ? '+' : ''}
                      ${selectedStock.quote.change.toFixed(2)} ({selectedStock.quote.change_percent.toFixed(2)}%)
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="content-section">
              <div className="section-content">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">AI Analysis</h2>
                <EnhancedAIAnalysis
                  ticker={selectedStock.quote.symbol}
                  companyName={selectedStock.metrics.name}
                  currentPrice={selectedStock.quote.price}
                  marketCap={selectedStock.metrics.marketCap}
                  financialMetrics={selectedStock.metrics}
                />
              </div>
            </section>
          </>
        )}

        {!selectedStock && !loading && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6 lg:p-8 text-center">
            <svg className="w-12 h-12 sm:w-16 sm:h-16 text-blue-600 mx-auto mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg sm:text-xl font-semibold text-blue-900 mb-2 sm:mb-3">Get Started</h3>
            <p className="text-sm sm:text-base text-blue-700 max-w-2xl mx-auto">
              Search for a stock symbol above or click on one of the popular stocks to see the enhanced AI analysis in action.
              The analysis includes comprehensive fundamental valuation, technical indicators, and an interactive chat interface.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};